import "./PriceRangeChipsComponentStyles.scss";
import { FC, useEffect, useRef, useState } from "react";
import { RangeSlider } from "rsuite";
import "rsuite/dist/rsuite.css";
import PriceRangeChipsComponentProps, { PriceRangeEntity } from "./PriceRangeChipsComponentProps";

export const PRICE_MAX = 1000000000;

// Extremos visibles del slider. El tope se muestra como "+300M": llevar el control hasta
// la derecha equivale a no poner techo, así el inventario más costoso nunca queda por fuera.
export const PRICE_SLIDER_MIN = 1000000;
export const PRICE_SLIDER_MAX = 300000000;
const PRICE_SLIDER_STEP = 1000000;

// Espera antes de aplicar el rango. El slider se mueve con estado local, así que arrastrar
// no re-renderiza el listado ni dispara peticiones: eso es lo que lo trababa en equipos
// de menos recursos. Al soltar se aplica de una vez.
const PRICE_COMMIT_DELAY = 500;

// Rangos continuos: el máximo de cada rango es el mínimo del siguiente, así ningún
// vehículo del inventario queda por fuera de todos los chips.
export const PRICE_RANGES: PriceRangeEntity[] = [
    { label: "Hasta $20M", min: 0, max: 20000000 },
    { label: "$20M – $50M", min: 20000000, max: 50000000 },
    { label: "$50M – $90M", min: 50000000, max: 90000000 },
    { label: "$90M – $150M", min: 90000000, max: 150000000 },
    { label: "Más de $150M", min: 150000000, max: PRICE_MAX },
];

// Chips que caben bajo un tope de precio; el último se recorta al tope.
export const rangosDePrecioHasta = (tope: number | undefined): PriceRangeEntity[] => {
    if (tope === undefined) return PRICE_RANGES;
    return PRICE_RANGES.filter((range) => range.min < tope).map((range) =>
        range.max <= tope
            ? range
            : { label: `$${range.min / 1000000}M – $${tope / 1000000}M`, min: range.min, max: tope },
    );
};

const _hasValue = (value: any): boolean => value !== undefined && value !== null && value !== "";

const _toNumber = (value: any): number | undefined => {
    if (!_hasValue(value)) return undefined;
    const number = typeof value === "number" ? value : parseInt(String(value).replace(/[^0-9]/g, ""), 10);
    return isNaN(number) ? undefined : number;
};

const _formatCurrency = (value: any): string => {
    const number = _toNumber(value);
    return number === undefined ? "" : `$ ${number.toLocaleString("es-CO")}`;
};

// Posición del slider para un precio guardado. Sin tope se muestra arriba del todo.
const _toSliderRange = (price: any): [number, number] => {
    const min = _toNumber(price?.min);
    const max = _toNumber(price?.max);
    const upper = max === undefined || max >= PRICE_SLIDER_MAX ? PRICE_SLIDER_MAX : Math.max(max, PRICE_SLIDER_MIN);
    const lower = min === undefined ? PRICE_SLIDER_MIN : Math.min(Math.max(min, PRICE_SLIDER_MIN), upper);
    return [lower, upper];
};

const PriceRangeChipsComponent: FC<PriceRangeChipsComponentProps> = ({
    formFunctions,
    keyName = "price",
    ranges = PRICE_RANGES,
    max = PRICE_MAX,
}) => {
    const { setValue, watch } = formFunctions;
    const price = watch(keyName);

    const selectedRange = ranges.find((range) => price?.min === range.min && price?.max === range.max);

    // Lo que se ve mientras se arrastra. Se resincroniza cuando el precio cambia por fuera
    // del slider (un chip, la URL o el botón de limpiar filtros).
    const [draft, setDraft] = useState<[number, number]>(() => _toSliderRange(price));
    const commitTimeout = useRef<ReturnType<typeof setTimeout>>();
    const dragging = useRef(false);

    const storedRange = _toSliderRange(price);
    useEffect(() => {
        if (dragging.current) return;
        setDraft(storedRange);
    }, [storedRange[0], storedRange[1]]);

    useEffect(() => () => clearTimeout(commitTimeout.current), []);

    const _syncQueryParams = (nextMin?: any, nextMax?: any) => {
        const url = new URL(window.location.href);
        if (_hasValue(nextMin)) url.searchParams.set("min_price", String(nextMin));
        else url.searchParams.delete("min_price");
        if (_hasValue(nextMax)) url.searchParams.set("max_price", String(nextMax));
        else url.searchParams.delete("max_price");
        window.history.replaceState({}, "", url.toString());
    };

    // Un chip manda sobre lo que quedara pendiente del slider, si no el temporizador
    // lo pisaría medio segundo después.
    const _cancelPendingCommit = () => {
        clearTimeout(commitTimeout.current);
        dragging.current = false;
    };

    const _handleClearPrice = () => {
        _cancelPendingCommit();
        setValue(keyName, undefined);
        _syncQueryParams(undefined, undefined);
    };

    const _handleSelectRange = (range: PriceRangeEntity) => {
        // Volver a tocar el rango activo quita el filtro, igual que el resto de chips.
        if (selectedRange === range) {
            _handleClearPrice();
            return;
        }
        _cancelPendingCommit();
        setValue(keyName, { min: range.min, max: range.max });
        _syncQueryParams(range.min, range.max);
    };

    const _commitRange = (value: [number, number]) => {
        clearTimeout(commitTimeout.current);
        dragging.current = false;
        const nextMin = value[0];
        // El tope del slider representa "sin límite superior", para no dejar fuera el
        // inventario más costoso.
        const nextMax = value[1] >= PRICE_SLIDER_MAX ? max : value[1];
        setValue(keyName, { min: nextMin, max: nextMax });
        _syncQueryParams(nextMin, nextMax);
    };

    const _handleChangeSlider = (value: number[]) => {
        const next: [number, number] = [value[0], value[1]];
        dragging.current = true;
        setDraft(next);
        clearTimeout(commitTimeout.current);
        commitTimeout.current = setTimeout(() => _commitRange(next), PRICE_COMMIT_DELAY);
    };

    return (
        <div className="price_range_chips">
            <span className="chips_label">Rangos populares</span>
            <div className="chips_container">
                {ranges.map((range, index) => (
                    <button
                        key={index}
                        type="button"
                        className={`price_chip ${selectedRange === range ? "active" : ""}`}
                        onClick={() => _handleSelectRange(range)}
                    >
                        {range.label}
                    </button>
                ))}
            </div>

            <div className="range_header">
                <span className="range_title">Rango de precio</span>
                <span className="range_values">
                    {_formatCurrency(draft[0])} — {_formatCurrency(draft[1])}
                    {draft[1] >= PRICE_SLIDER_MAX ? "+" : ""}
                </span>
            </div>

            <RangeSlider
                className="price_range_slider"
                value={draft}
                min={PRICE_SLIDER_MIN}
                max={PRICE_SLIDER_MAX}
                step={PRICE_SLIDER_STEP}
                renderTooltip={(value) => _formatCurrency(value)}
                onChange={_handleChangeSlider}
                onChangeCommitted={(value: number[]) => _commitRange([value[0], value[1]])}
            />
            <div className="range_bounds">
                <span>$1M</span>
                <span>+300M</span>
            </div>
        </div>
    );
};

export default PriceRangeChipsComponent;
